import { formatSizeBreakdown, type IssuedQuoteVersion } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { cn } from '../lib/utils';

/** 견적서 배송지 표 한 줄 — 견적 배송지·주문 배송지가 같은 모양으로 넘긴다. */
export interface QuoteDocumentDestination {
  /** 발행본 배송비 줄(`destinationId`)과 맞출 견적 배송지 id — 짝이 없으면 null(배송비 칸 '-'). */
  readonly quoteDestinationId: string | null;
  /** 비회원 요청 견적 배송지는 받는 분·연락처·상세 주소가 없다(주문할 때 받는다). */
  readonly recipientName: string | null;
  readonly recipientPhone: string | null;
  readonly postalCode: string;
  readonly address1: string;
  readonly address2: string | null;
  readonly quantity: number;
  readonly sizes: readonly { sizeName: string; quantity: number }[];
}

export interface QuoteDocumentProps {
  /** 발행본 DTO — 초안·내부 메모·이메일은 이 타입에 없어 구조적으로 인쇄에 섞이지 않는다. */
  readonly version: IssuedQuoteVersion;
  readonly quoteNumber: string;
  /** 견적 요청 시각(ISO). 주면 머리말에 요청일을 적는다. */
  readonly requestedAt?: string;
  /** 배송지 표 — 주지 않거나 비면 표를 그리지 않는다(비회원 링크는 주소를 보이지 않는다). */
  readonly destinations?: readonly QuoteDocumentDestination[];
  readonly className?: string;
}

const KO_NUMBER = new Intl.NumberFormat('ko-KR');
/** 발행일·요청일(시각) — 브라우저 시간대가 아니라 한국 날짜로 적는다(만료일과 같은 기준). */
const KO_DATE_KST = new Intl.DateTimeFormat('ko-KR', {
  dateStyle: 'medium',
  timeZone: 'Asia/Seoul',
});
/** 만료일(달력 날짜) — UTC 자정으로 만들고 UTC 로 적어 어느 시간대에서도 날짜가 밀리지 않는다. */
const KO_CALENDAR_DATE = new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeZone: 'UTC' });

const formatWon = (amount: number): string => `${KO_NUMBER.format(amount)}원`;

const formatDateTime = (iso: string): string => KO_DATE_KST.format(new Date(iso));

/** YYYY-MM-DD 달력 날짜 — 시간대 변환 없이 그 날짜 그대로 적는다. */
const formatCalendarDate = (date: string): string => {
  const [year, month, day] = date.split('-').map(Number);
  return KO_CALENDAR_DATE.format(new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1)));
};

const formatAddress = ({ postalCode, address1, address2 }: QuoteDocumentDestination): string =>
  `(${postalCode}) ${address1}${address2 === null || address2 === '' ? '' : ` ${address2}`}`;

const recipientOf = ({ recipientName, recipientPhone }: QuoteDocumentDestination): string =>
  recipientName === null ? '주문 때 입력' : `${recipientName} · ${recipientPhone ?? ''}`;

const CELL_CLASS = 'px-3 py-2';
const NUMERIC_CELL_CLASS = `${CELL_CLASS} text-right tabular-nums`;
const TERM_CLASS = 'text-xs text-muted-foreground print:text-black';
const DESCRIPTION_CLASS = 'text-sm whitespace-pre-wrap';

/**
 * 고객·운영자가 함께 쓰는 견적서 표시·인쇄 컴포넌트(견적 설계 §7-10).
 *
 * 공급자·수신자·품목·세액·조건·공통 문구는 모두 발행 snapshot(version)에서 읽는다(고객 상세·인쇄·관리자 발행본 보기가 모두 이
 * 컴포넌트다). 공통 문구 버전(`termsVersion`)은 동의 기록용 저장값이라 견적서에 싣지 않는다 — 운영자 기록 화면(관리자 주문 상세)만 보여 준다.
 * 인쇄는 소비 앱이 `window.print()` 를 부르고, 여기서는 `print:` 변형으로 인쇄 모양만 맡는다.
 */
export const QuoteDocument = ({
  version,
  quoteNumber,
  requestedAt,
  destinations = [],
  className,
}: QuoteDocumentProps): ReactElement => {
  const productLines = version.lines.filter((line) => line.kind === 'PRODUCT');
  const shippingLines = version.lines.filter((line) => line.kind === 'SHIPPING');
  const feeOf = (quoteDestinationId: string | null): string => {
    const line =
      quoteDestinationId === null
        ? undefined
        : shippingLines.find((candidate) => candidate.destinationId === quoteDestinationId);
    return line === undefined ? '-' : formatWon(line.amount);
  };
  return (
    <article
      aria-label="견적서"
      className={cn(
        'flex flex-col gap-8 rounded-lg border bg-background p-6 text-foreground break-keep',
        'print:gap-6 print:rounded-none print:border-0 print:bg-white print:p-0 print:text-black print:shadow-none',
        className,
      )}
    >
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">견적서</h2>
          <p className="mt-1 text-sm text-muted-foreground print:text-black">
            견적 번호 {quoteNumber} · {version.version}차 발행
          </p>
        </div>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {requestedAt !== undefined ? (
            <>
              <dt className={TERM_CLASS}>요청일</dt>
              <dd>{formatDateTime(requestedAt)}</dd>
            </>
          ) : null}
          <dt className={TERM_CLASS}>발행일</dt>
          <dd>{formatDateTime(version.issuedAt)}</dd>
          <dt className={TERM_CLASS}>만료일</dt>
          <dd>{formatCalendarDate(version.validUntil)}</dd>
        </dl>
      </header>

      <div className="grid gap-6 sm:grid-cols-2">
        <section aria-label="공급자" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">공급자</h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className={TERM_CLASS}>상호</dt>
            <dd>{version.issuer.companyName}</dd>
            <dt className={TERM_CLASS}>사업자등록번호</dt>
            <dd>{version.issuer.businessRegistrationNumber}</dd>
            <dt className={TERM_CLASS}>대표자</dt>
            <dd>{version.issuer.representativeName}</dd>
            <dt className={TERM_CLASS}>주소</dt>
            <dd>{version.issuer.address}</dd>
            <dt className={TERM_CLASS}>연락처</dt>
            <dd>{version.issuer.contact}</dd>
          </dl>
        </section>

        <section aria-label="수신자" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">수신자</h3>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {version.recipientCompanyName !== null ? (
              <>
                <dt className={TERM_CLASS}>회사·단체명</dt>
                <dd>{version.recipientCompanyName}</dd>
              </>
            ) : null}
            <dt className={TERM_CLASS}>담당자</dt>
            <dd>{version.recipientContactName}</dd>
          </dl>
        </section>
      </div>

      {destinations.length > 0 ? (
        <section aria-label="배송지 목록" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">배송지 {destinations.length}곳</h3>
          <div className="overflow-x-auto rounded-md border print:overflow-visible print:rounded-none">
            <table aria-label="배송지" className="w-full text-sm">
              <thead className="bg-muted/50 text-left print:bg-transparent">
                <tr className="border-b">
                  <th scope="col" className={`${CELL_CLASS} font-medium`}>
                    받는 분
                  </th>
                  <th scope="col" className={`${CELL_CLASS} font-medium`}>
                    주소
                  </th>
                  <th scope="col" className={`${NUMERIC_CELL_CLASS} font-medium`}>
                    수량
                  </th>
                  <th scope="col" className={`${NUMERIC_CELL_CLASS} font-medium`}>
                    배송비
                  </th>
                </tr>
              </thead>
              <tbody>
                {destinations.map((destination, index) => (
                  <tr
                    // biome-ignore lint/suspicious/noArrayIndexKey: 읽기 전용 표 — 순서가 곧 배송지 번호다(같은 주소가 두 번 올 수 있다).
                    key={index}
                    className="border-b last:border-b-0"
                  >
                    <td className={CELL_CLASS}>{recipientOf(destination)}</td>
                    <td className={CELL_CLASS}>{formatAddress(destination)}</td>
                    <td className={NUMERIC_CELL_CLASS}>
                      {KO_NUMBER.format(destination.quantity)}개
                      {destination.sizes.length > 0 ? (
                        <p className="mt-1 text-xs text-muted-foreground print:text-black">
                          {formatSizeBreakdown(destination.sizes)}
                        </p>
                      ) : null}
                    </td>
                    <td className={NUMERIC_CELL_CLASS}>{feeOf(destination.quoteDestinationId)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section aria-label="품목과 금액" className="flex flex-col gap-3">
        <div className="overflow-x-auto rounded-md border print:overflow-visible print:rounded-none">
          <table aria-label="품목" className="w-full text-sm">
            <thead className="bg-muted/50 text-left print:bg-transparent">
              <tr className="border-b">
                <th scope="col" className={`${CELL_CLASS} font-medium`}>
                  품목
                </th>
                <th scope="col" className={`${NUMERIC_CELL_CLASS} font-medium`}>
                  수량
                </th>
                <th scope="col" className={`${NUMERIC_CELL_CLASS} font-medium`}>
                  단가
                </th>
                <th scope="col" className={`${NUMERIC_CELL_CLASS} font-medium`}>
                  금액
                </th>
              </tr>
            </thead>
            <tbody>
              {productLines.map((line) => (
                <tr key={line.id} className="border-b last:border-b-0">
                  <td className={CELL_CLASS}>{line.name}</td>
                  <td className={NUMERIC_CELL_CLASS}>{KO_NUMBER.format(line.quantity)}</td>
                  <td className={NUMERIC_CELL_CLASS}>{formatWon(line.unitPrice)}</td>
                  <td className={NUMERIC_CELL_CLASS}>{formatWon(line.amount)}</td>
                </tr>
              ))}
            </tbody>
            {shippingLines.length > 0 ? (
              <tbody className="border-t">
                <tr className="border-b">
                  <th
                    scope="rowgroup"
                    colSpan={4}
                    className={`${CELL_CLASS} bg-muted/30 text-left text-xs font-semibold print:bg-transparent`}
                  >
                    배송비
                  </th>
                </tr>
                {shippingLines.map((line) => (
                  <tr key={line.id} className="border-b last:border-b-0">
                    <td className={CELL_CLASS}>{line.name}</td>
                    <td className={NUMERIC_CELL_CLASS}>{KO_NUMBER.format(line.quantity)}</td>
                    <td className={NUMERIC_CELL_CLASS}>{formatWon(line.unitPrice)}</td>
                    <td className={NUMERIC_CELL_CLASS}>{formatWon(line.amount)}</td>
                  </tr>
                ))}
              </tbody>
            ) : null}
          </table>
        </div>
        <dl className="ml-auto grid grid-cols-[auto_auto] gap-x-6 gap-y-1 text-sm tabular-nums">
          <dt className={TERM_CLASS}>상품 금액</dt>
          <dd className="text-right">{formatWon(version.productAmount)}</dd>
          <dt className={TERM_CLASS}>배송비</dt>
          <dd className="text-right">{formatWon(version.shippingAmount)}</dd>
          <dt className={TERM_CLASS}>공급가액</dt>
          <dd className="text-right">{formatWon(version.supplyAmount)}</dd>
          <dt className={TERM_CLASS}>부가세</dt>
          <dd className="text-right">{formatWon(version.vatAmount)}</dd>
          <dt className="text-sm font-semibold">합계(부가세 포함)</dt>
          <dd className="text-right text-base font-semibold">{formatWon(version.totalAmount)}</dd>
        </dl>
      </section>

      <section aria-label="거래 조건" className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">거래 조건</h3>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className={TERM_CLASS}>제작 착수 시점</dt>
          <dd className={DESCRIPTION_CLASS}>{version.productionStartNote}</dd>
          <dt className={TERM_CLASS}>납기</dt>
          <dd className={DESCRIPTION_CLASS}>{version.deliveryNote}</dd>
          <dt className={TERM_CLASS}>공급 방법</dt>
          <dd className={DESCRIPTION_CLASS}>{version.deliveryMethodNote}</dd>
          <dt className={TERM_CLASS}>특약 사항</dt>
          <dd className={DESCRIPTION_CLASS}>{version.specialTerms ?? '없음'}</dd>
        </dl>
      </section>

      <footer className="flex flex-col gap-1 border-t pt-4 text-xs text-muted-foreground print:text-black">
        <p className="whitespace-pre-wrap">{version.termsText}</p>
      </footer>
    </article>
  );
};
